import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { format } from "date-fns";
import { useApi } from "../../hooks/useApi";

import { formatTime } from "../../utils/functions";

const AvailableTimes = ({
  barber,
  service,
  selected,
  selectedSlot,
  setSelectedSlot,
}) => {
  const api = useApi();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState(null);

  useEffect(() => {
    if (!barber?.user_id || !service?.service_id || !selected) return;

    const dateStr = format(selected, "yyyy-MM-dd");

    const load = async () => {
      setLoading(true);
      setError(null);
      setReason(null);
      setSlots([]);
      setSelectedSlot(null);

      try {
        const res = await api(
          `/bookings/availability.php?barber_id=${barber.user_id}&service_id=${service.service_id}&date=${dateStr}`,
          { method: "GET" },
        );

        if (!res.data.available) {
          setReason(res.data.reason || "No availability on this day.");
        } else {
          setSlots(res.data.slots || []);
        }
      } catch (e) {
        setError(e.message || "Failed to load availability.");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [barber?.user_id, service?.service_id, selected]);

  // Split slots into AM and PM
  const amSlots = slots.filter((s) => {
    const hour = parseInt(s.start.split(":")[0], 10);
    return hour < 12;
  });
  const pmSlots = slots.filter((s) => {
    const hour = parseInt(s.start.split(":")[0], 10);
    return hour >= 12;
  });

  return (
    <Box
      className="calendar-shell"
      sx={{ display: "flex", flexDirection: "column" }}
    >
      <Typography variant="body1" sx={{ color: "text.third" }}>
        {selected
          ? selected.toLocaleDateString("en-US", {
              weekday: "long",
              month: "long",
              day: "numeric",
            })
          : "Select a date"}
      </Typography>
      {barber && service && (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          with{" "}
          <strong>
            {barber.first_name} {barber.last_name}
          </strong>{" "}
          — {service.name}
        </Typography>
      )}

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 3 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Checking availability...
          </Typography>
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && reason && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {reason}
          </Typography>
        </Box>
      )}

      {!loading && !error && !reason && slots.length === 0 && selected && (
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 3 }}>
          No available times on this day.
        </Typography>
      )}

      {!loading && amSlots.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 3, fontWeight: 500 }}
          >
            MORNING
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {amSlots.map((slot) => {
              const isSelected = selectedSlot?.start_utc === slot.start_utc;
              return (
                <Grid key={slot.start_utc} size={4}>
                  <Button
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => setSelectedSlot(slot)}
                    sx={{ width: "100%", fontSize: "0.8rem" }}
                  >
                    {formatTime(slot.start)}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {!loading && pmSlots.length > 0 && (
        <>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 3, fontWeight: 500 }}
          >
            AFTERNOON
          </Typography>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {pmSlots.map((slot) => {
              const isSelected = selectedSlot?.start_utc === slot.start_utc;
              return (
                <Grid key={slot.start_utc} size={4}>
                  <Button
                    variant={isSelected ? "contained" : "outlined"}
                    onClick={() => setSelectedSlot(slot)}
                    sx={{ width: "100%", fontSize: "0.8rem" }}
                  >
                    {formatTime(slot.start)}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default AvailableTimes;
